import * as Types from "../Actions/Types"
import { State } from "../Reducers"
import { EventTypes, createTracker } from "redux-segment"
import _get from "lodash/get"
import _set from "lodash/set"
import { createClient, EntryFields, EntryCollection, Entry } from "contentful"
import { config } from "bridger"
import { safePromise } from "../Helpers/Functions/promise"
import { ContentType } from "../Models/External/Contentful"
import { TrackingType } from "../Actions/Types"
import { isDoNotTrackEnabled } from "../Helpers/Functions/analytics"
import { Middleware } from "redux"

export type EventType = "identify" | "page" | "track" | "alias" | "group"

interface TrackablePayload {
    property_path: string
    state_path?: string
    action_path?: string
    default_value?: string
}

interface TrackableConditions {
    state_path?: string
    action_path?: string
    state_value_regex?: RegExp
    action_value_regex?: RegExp
}

interface Trackable {
    type: EventType
    payload?: TrackablePayload[]
    conditions?: TrackableConditions[]
}

interface Tracking {
    [action: string]: Trackable[]
}

interface EventPayloadFields {
    property_path: EntryFields.Text
    state_path: EntryFields.Text
    action_path: EntryFields.Text
    default_value: EntryFields.Text
}

interface ConditionsField {
    state_path: EntryFields.Text
    action_path: EntryFields.Text
    state_value_regex: EntryFields.Text
    action_value_regex: EntryFields.Text
}

interface EventFields {
    action_name: EntryFields.Text
    event_type: EventType
    event_payload: EntryFields.Link<Entry<EventPayloadFields>>[]
    conditions: EntryFields.Link<Entry<ConditionsField>>[]
}

async function getTrackingData() {
    const client = createClient({
        space: config.dash.contentful.spaceID,
        accessToken: config.dash.contentful.accessToken,
        environment: config.dash.contentful.environment
    })
    return await safePromise(
        client.getEntries<EventFields>({
            content_type: ContentType.tracking,
            include: 1
        })
    )
}

function getLinkedDataFn(data: EntryCollection<EventFields>) {
    return <T>(entry: EntryFields.Link<Entry<T>>) => {
        const payload: Entry<T> =
            data.includes &&
            data.includes.Entry &&
            data.includes.Entry.find(
                (inc: Entry<T>) => inc.sys.id === entry.sys.id
            )
        return payload ? payload.fields : undefined
    }
}

const routerActions = [
    "@@router/INIT_PATH",
    "@@router/UPDATE_PATH",
    "@@router/LOCATION_CHANGE",
    "@@reduxReactRouter/initRoutes",
    "@@reduxReactRouter/routerDidChange",
    "@@reduxReactRouter/replaceRoutes"
]

function transformContentful(data: EntryCollection<EventFields>): {
    valid: Tracking
    errorActions: string[]
} {
    const availableTypes = Object.values(Types).reduce(
        (arr: string[], obj) => [...arr, ...Object.values(obj)],
        []
    )
    availableTypes.push(...routerActions)
    const getLinkedData = getLinkedDataFn(data)
    const errorActions: string[] = []
    const valid: Tracking = data.items.reduce((obj: Tracking, item) => {
        if (!availableTypes.includes(item.fields.action_name)) {
            if (!errorActions.includes(item.fields.action_name))
                errorActions.push(item.fields.action_name)
            return obj
        }
        const track: Trackable[] = [
            ...(obj[item.fields.action_name] || []),
            {
                type: item.fields.event_type,
                payload: item.fields.event_payload
                    ? item.fields.event_payload.reduce<TrackablePayload[]>(
                          (pre, p) => {
                              const d = getLinkedData<EventPayloadFields>(p)
                              return d ? [...pre, d] : pre
                          },
                          []
                      )
                    : undefined,
                conditions: item.fields.conditions
                    ? item.fields.conditions.reduce<TrackableConditions[]>(
                          (pre, c) => {
                              const link = getLinkedData<ConditionsField>(c)
                              if (!link) return pre
                              const actionValueRegex = link.action_value_regex
                                  ? RegExp(link.action_value_regex)
                                  : undefined
                              const stateValueRegex = link.state_value_regex
                                  ? RegExp(link.state_value_regex)
                                  : undefined
                              return [
                                  ...pre,
                                  {
                                      action_value_regex: actionValueRegex,
                                      state_value_regex: stateValueRegex,
                                      action_path: link.action_path,
                                      state_path: link.state_path
                                  }
                              ]
                          },
                          [] as TrackableConditions[]
                      )
                    : undefined
            }
        ]
        return {
            ...obj,
            [item.fields.action_name]: track
        }
    }, {})
    return { valid, errorActions }
}

function getValue(obj?: any, path?: string) {
    return path && obj ? _get(obj, path) : undefined
}

function areConditionsMet(
    state?: State,
    payload?: any,
    conditions?: TrackableConditions[]
) {
    if (!conditions) return true

    for (const condition of conditions) {
        if (
            condition.action_path === undefined &&
            condition.state_path === undefined
        )
            continue
        const aVal = getValue(payload, condition.action_path)
        const sVal = getValue(state, condition.state_path)
        const aTrue = condition.action_value_regex
            ? condition.action_value_regex.test(aVal)
            : true
        const sTrue = condition.state_value_regex
            ? condition.state_value_regex.test(sVal)
            : true
        if (!(aTrue && sTrue)) return false
    }

    return true
}

/**
 * group all actions
 * provide list of valid actions (grouped)
 * provide list of invalid action names
 * create map
 * create error event
 */

async function trackerMapper(missedEvents: any[]) {
    const { data, error } = await getTrackingData()
    if (error || data === undefined) return undefined
    const { errorActions, valid } = transformContentful(data)
    const mapper: {
        [index: string]: (
            getState: () => State,
            action: any,
            missed?: boolean
        ) => {
            eventType: string
            eventPayload?: { [index: string]: any }
        }[]
    } = {}
    Object.entries(valid).forEach(t => {
        function mapFn(getState: () => State, action: any, missed?: boolean) {
            return t[1].reduce((pre, e) => {
                const state = getState()
                if (!areConditionsMet(state, action.payload, e.conditions))
                    return pre
                const payload: { [index: string]: any } | undefined = e.payload
                    ? {}
                    : undefined
                if (e.payload && payload)
                    e.payload.forEach(item => {
                        const value = [
                            getValue(action.payload, item.action_path),
                            getValue(state, item.state_path),
                            item.default_value,
                            "Undefined value"
                        ].find(v => v !== undefined)
                        _set(payload, item.property_path, value)
                    })
                return [
                    ...pre,
                    {
                        eventType: EventTypes[e.type],
                        eventPayload: {
                            ...payload,
                            properties: {
                                ...(payload && payload.properties),
                                missed
                            }
                        }
                    }
                ]
            }, [] as any[])
        }
        mapper[t[0]] = mapFn
    })
    // add error type list to @@INIT event
    const base = mapper[Types.TrackingType.init]
    function fn(getState: () => State, action: any) {
        const baseEvents = base && base(getState, action)
        const existing =
            baseEvents && baseEvents.length > 0 ? [...baseEvents] : []

        missedEvents.forEach(e => {
            const missed = mapper[e.type] && mapper[e.type](getState, e, true)
            if (missed && missed.length > 0) {
                existing.push(...missed)
            }
        })

        const event = {
            eventType: EventTypes.track,
            eventPayload: {
                properties: {
                    invalidTypes:
                        errorActions.length > 0 ? errorActions : undefined
                }
            }
        }
        return [...existing, event]
    }
    mapper[Types.TrackingType.init] = fn
    return { skipDefaultMapping: true, mapper }
}

export const TrackerMiddleware: () => Middleware = () => {
    let track: any
    // this can be used later to switch tracking on and off
    let noTrack = isDoNotTrackEnabled()
    const missedEvents: any[] = []
    let init = false
    trackerMapper(missedEvents)
        .then(mapper => {
            track = createTracker(mapper)
        })
        .catch(_ => {
            noTrack = true
        })

    return store => next => action => {
        if (track && !noTrack) {
            if (!init) {
                init = true
                store.dispatch({ type: TrackingType.init })
            }
            return track(store)(next)(action)
        } else {
            if (!noTrack) missedEvents.push(action)
            return next(action)
        }
    }
}
